import React, {useContext, useEffect, useRef, useState} from 'react';
import {Modal} from 'react-bootstrap';
import Swal from '../../utils/swal';
import AppContext from "../../../appContext";
import {useApi} from "../../../hooks/useApi";
import './midia-labs-form.css';
import CustomButton from "../../../components/buttons/button";
import {CloudUpload, FileEarmark, Image, X} from "react-bootstrap-icons";
import {TextField, Tooltip, InputAdornment} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MESSAGES from "../../../shared/constants/messages";
import {Base64Converter} from "../../../shared/utils/Base64Converter";

const MidiaLabsForm = ({midia, labCnpj, onClose, onSave, onSaveComplete}) => {
  const context = useContext(AppContext);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef(null);
  const {post} = useApi();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState({thumbnail: false, arquivo: false});

  const getFileType = (url) => {
    if (!url) return null;
    const lowerUrl = url.toLowerCase();

    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    if (videoExtensions.some(ext => lowerUrl.includes(ext))) return 'video';

    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    if (imageExtensions.some(ext => lowerUrl.includes(ext))) return 'image';

    return 'document';
  };

  const [formData, setFormData] = useState({
    nome: midia?.nome ? Base64Converter.decode(midia.nome) : '',
    link: midia?.link || '',
    thumbnailFile: null,
    arquivoFile: null,
    thumbnailPreview: midia?.thumbnail_url || null,
    arquivoUrl: midia?.arquivo_url || null,
    arquivoPreview: midia?.arquivo_url || null,
    arquivoPreviewType: getFileType(midia?.arquivo_url),
    originalThumbnailName: midia?.thumbnail_filename ? Base64Converter.decode(midia.thumbnail_filename) : null,
    originalArquivoName: midia?.arquivo_filename ? Base64Converter.decode(midia.arquivo_filename) : null
  });

  useEffect(() => {
    if (midia?.arquivo_url) {
      const url = midia.arquivo_url.toLowerCase();

      const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
      const isVideo = videoExtensions.some(ext => url.includes(ext));

      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      const isImage = imageExtensions.some(ext => url.includes(ext));

      setFormData(prev => ({
        ...prev,
        arquivoPreview: midia.arquivo_url,
        arquivoPreviewType: isVideo ? 'video' : (isImage ? 'image' : 'document')
      }));
    }
  }, [midia]);

  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = MESSAGES.MIDIA_LABS_FORM_NAME_REQUIRED;
    }

    if (!midia && !formData.thumbnailFile) {
      newErrors.thumbnail = MESSAGES.MIDIA_LABS_FORM_THUMBNAIL_REQUIRED;
    }

    if (formData.link.trim() && !isValidUrl(formData.link)) {
      newErrors.link = MESSAGES.MIDIA_LABS_FORM_INVALID_URL;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFormData(prev => ({...prev, [name]: value}));

    if (errors[name]) {
      setErrors(prev => ({...prev, [name]: ''}));
    }

    if (name === 'link' && value.trim() && errors.arquivo === MESSAGES.MIDIA_LABS_FORM_FILE_OR_LINK_REQUIRED) {
      setErrors(prev => ({...prev, arquivo: ''}));
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (fileType === 'thumbnail') {
      if (!file.type.startsWith('image/')) {
        Swal.fire(MESSAGES.MIDIA_LABS_FORM_ERROR, MESSAGES.MIDIA_LABS_FORM_THUMBNAIL_ERROR, 'error');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          thumbnailFile: file,
          thumbnailPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    } else {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            arquivoFile: file,
            arquivoPreview: reader.result,
            arquivoPreviewType: 'image'
          }));
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        const videoUrl = URL.createObjectURL(file);
        setFormData(prev => ({
          ...prev,
          arquivoFile: file,
          arquivoPreview: videoUrl,
          arquivoPreviewType: 'video'
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          arquivoFile: file,
          arquivoPreview: 'document',
          arquivoPreviewType: 'document'
        }));
      }
    }

    if (errors[fileType]) {
      setErrors(prev => ({...prev, [fileType]: ''}));
    }

    if (fileType === 'arquivo' && errors.link === MESSAGES.MIDIA_LABS_FORM_LINK_OR_FILE_REQUIRED) {
      setErrors(prev => ({...prev, link: ''}));
    }
  };

  const truncateFilename = (filename, maxLength = 30) => {
    if (!filename || filename.length <= maxLength) return filename;

    const extension = filename.lastIndexOf('.') > -1
      ? filename.slice(filename.lastIndexOf('.'))
      : '';

    const nameWithoutExt = filename.slice(0, filename.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 3);

    return `${truncatedName}...${extension}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    context.showLoading(midia ? MESSAGES.MIDIA_LABS_FORM_UPDATING : MESSAGES.MIDIA_LABS_FORM_SAVING);

    const formDataToSend = new FormData();
    if (formData.thumbnailFile) {
      formDataToSend.append('thumbnail', formData.thumbnailFile);
    }
    if (formData.arquivoFile) {
      formDataToSend.append('arquivo', formData.arquivoFile);
    }

    try {
      const params = new URLSearchParams({
        lab_cnpj: labCnpj,
        nome: formData.nome,
        link: formData.link
      });

      if (midia) {
        params.append('midia_id', midia.id);
      }

      const endpoint = midia ? 'update_midia_labs.php' : 'create_midia_labs.php';

      const json = await post(
        `${process.env.REACT_APP_LABS_API}${endpoint}?${params}`,
        formDataToSend
      );

      if (json?.status === 200) {
        await onSave();
        context.hideLoading();
        await Swal.fire(MESSAGES.MIDIA_LABS_FORM_SUCCESS, json.message, 'success');
        onClose();
      } else {
        context.hideLoading();
        await Swal.fire(
          MESSAGES.MIDIA_LABS_FORM_ERROR,
          json?.message || MESSAGES.MIDIA_LABS_FORM_ERROR_SAVING,
          'error'
        );
      }
    } catch (error) {
      context.hideLoading();
      await Swal.fire(MESSAGES.MIDIA_LABS_FORM_ERROR, MESSAGES.MIDIA_LABS_FORM_ERROR_SAVING, 'error');
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = (inputId) => {
    document.getElementById(inputId).click();
  };

  const handleDragOver = (e, type) => {
    e.preventDefault();
    setIsDragging(prev => ({...prev, [type]: true}));
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    setIsDragging(prev => ({...prev, [type]: false}));
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    setIsDragging(prev => ({...prev, [type]: false}));
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange({target: {files: [file]}}, type);
    }
  };

  const handleMouseEnter = () => {
    tooltipTimeout.current = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
  };

  const handleMouseLeave = () => {
    clearTimeout(tooltipTimeout.current);
    setShowTooltip(false);
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered className="midia-labs-modal">
      <Modal.Header className="midia-labs-form__header">
        <h2 className="midia-labs-form__title">
          {midia ? MESSAGES.MIDIA_LABS_FORM_EDIT_TITLE : MESSAGES.MIDIA_LABS_FORM_CREATE_TITLE}
        </h2>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="midia-labs-form__wrapper">
          <form onSubmit={handleSubmit} id="midia-labs-form">
            <div className="midia-labs-form__section">
              <h3 className="midia-labs-form__section-title">{MESSAGES.MIDIA_LABS_FORM_INFO_SECTION}</h3>

              <div className="row">
                <div className="col-xs-12 col-md-6">
                  <div className="midia-labs-form__field">
                    <TextField
                      id="midia-labs-form-nome-input"
                      name="nome"
                      label={MESSAGES.MIDIA_LABS_FORM_NAME_LABEL}
                      variant="outlined"
                      className="lp-field"
                      value={formData.nome}
                      onChange={handleInputChange}
                      error={!!errors.nome}
                      helperText={errors.nome || ""}
                    />
                  </div>
                </div>
                <div className="col-xs-12 col-md-6">
                  <div className="midia-labs-form__field">
                    <TextField
                      id="midia-labs-form-link-input"
                      name="link"
                      label={MESSAGES.MIDIA_LABS_FORM_LINK_LABEL}
                      variant="outlined"
                      className="lp-field"
                      value={formData.link}
                      onChange={handleInputChange}
                      error={!!errors.link}
                      helperText={errors.link || ""}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={MESSAGES.MIDIA_LABS_FORM_LINK_TOOLTIP} placement="top" classes={{ tooltip: "midia-labs-tooltip" }}>
                              <InfoOutlinedIcon style={{ color: "#0066B3", cursor: "pointer" }} />
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="midia-labs-form__section">
              <h3 className="midia-labs-form__section-title">{MESSAGES.MIDIA_LABS_FORM_FILES_SECTION}</h3>

              <div className="row">
                <div className="col-xs-12 col-md-6">
                  <div className="midia-labs-form__field">
                    <div className="midia-labs-form__label-with-tooltip">
                      <label className="midia-labs-form__label">{MESSAGES.MIDIA_LABS_FORM_THUMBNAIL_LABEL}:</label>
                    </div>

                    <div
                      className={`midia-labs-form__dropzone ${isDragging.thumbnail ? 'dragging' : ''}`}
                      onDragOver={(e) => handleDragOver(e, 'thumbnail')}
                      onDragLeave={(e) => handleDragLeave(e, 'thumbnail')}
                      onDrop={(e) => handleDrop(e, 'thumbnail')}
                      onClick={() => triggerFileInput('thumbnailFile')}
                      id="midia-labs-form-thumbnail-dropzone">
                      <input
                        type="file"
                        id="thumbnailFile"
                        className="d-none"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'thumbnail')}
                      />

                      {formData.thumbnailPreview ? (
                        <div className="text-center position-relative">
                          <img
                            src={formData.thumbnailPreview}
                            alt="Preview"
                            className="midia-labs-form__preview-small"
                          />
                          <p className="midia-labs-form__filename">
                            {truncateFilename(formData.thumbnailFile?.name) ||
                              formData.originalThumbnailName ||
                              MESSAGES.MIDIA_LABS_FORM_FILE_LOADED
                            }
                          </p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="text-center">
                            <Image className="midia-labs-form__file-icon"/>
                            <p className="mb-2">{MESSAGES.MIDIA_LABS_FORM_DRAG_OR_CLICK}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {errors.thumbnail && <div className="text-danger small mt-1">{errors.thumbnail}</div>}
                  </div>
                </div>

                <div className="col-xs-12 col-md-6">
                  <div className="midia-labs-form__field">
                    <div className="midia-labs-form__label-with-tooltip">
                      <label className="midia-labs-form__label">{MESSAGES.MIDIA_LABS_FORM_FILE_LABEL}:</label>
                      <Tooltip title="Arquivo que estará disponível para download. Formatos suportados: imagens (JPG, PNG, WEBP) e vídeos (MP4, WEBM). Tamanho recomendado: até 10MB." placement="right" classes={{ tooltip: "midia-labs-tooltip" }}>
                        <InfoOutlinedIcon style={{ color: "#0066B3", cursor: "pointer", fontSize: 18 }} />
                      </Tooltip>
                    </div>

                    <div className="position-relative">
                      <div
                        className={`midia-labs-form__dropzone ${isDragging.arquivo ? 'dragging' : ''}`}
                        onDragOver={(e) => handleDragOver(e, 'arquivo')}
                        onDragLeave={(e) => handleDragLeave(e, 'arquivo')}
                        onDrop={(e) => handleDrop(e, 'arquivo')}
                        onClick={() => triggerFileInput('arquivoFile')}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        id="midia-labs-form-arquivo-dropzone">
                        <input
                          type="file"
                          id="arquivoFile"
                          className="d-none"
                          onChange={(e) => handleFileChange(e, 'arquivo')}
                        />
                        {formData.arquivoPreview ? (
                          <div className="text-center position-relative">
                            {formData.arquivoPreviewType === 'video' ? (
                              <video
                                src={formData.arquivoPreview}
                                className="midia-labs-form__preview-small"
                                muted
                              />
                            ) : formData.arquivoPreviewType === 'image' ? (
                              <img
                                src={formData.arquivoPreview}
                                alt="Preview"
                                className="midia-labs-form__preview-small"
                              />
                            ) : (
                              <FileEarmark className="midia-labs-form__file-icon"/>
                            )}
                            <p className="midia-labs-form__filename">
                              {truncateFilename(formData.arquivoFile?.name || formData.originalArquivoName) ||
                                MESSAGES.MIDIA_LABS_FORM_FILE_LOADED
                              }
                            </p>
                          </div>
                        ) : (
                          <div className="text-center">
                            <div className="text-center">
                              <CloudUpload className="midia-labs-form__file-icon"/>
                              <p className="mb-2">{MESSAGES.MIDIA_LABS_FORM_DRAG_OR_CLICK}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      {showTooltip && (
                        <div className="midia-labs-form__tooltip">
                          Arquivo que estará disponível para download
                        </div>
                      )}
                    </div>
                    {errors.arquivo && <div className="text-danger small mt-1">{errors.arquivo}</div>}
                  </div>
                </div>
              </div>
            </div>

          </form>
        </div>
      </Modal.Body>
      <Modal.Footer className="midia-labs-form__footer">
        <div className="midia-labs-form__cancel-button">
          <CustomButton
            label={MESSAGES.MIDIA_LABS_FORM_CANCEL}
            width="140px"
            blue_button={true}
            onClick={onClose}
            type="button"
            marginTop="0"
            id="midia-labs-form-cancel-btn"
          />
        </div>
        <div className="midia-labs-form__upload-button">
          <CustomButton
            onClick={handleSubmit}
            label={loading ? MESSAGES.MIDIA_LABS_FORM_SAVING_BUTTON : MESSAGES.MIDIA_LABS_FORM_SAVE}
            width="140px"
            blue_button={true}
            type="submit"
            disabled={loading}
            marginTop="0"
            id="midia-labs-form-save-btn"
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default MidiaLabsForm;